import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCode, 
  FaProjectDiagram, 
  FaUsers, 
  FaStar,
  FaRocket,
  FaAward,
  FaCalendarAlt,
  FaGithub
} from 'react-icons/fa';
import { apiService } from '../../services/api';

const Stats = () => {
  const [stats, setStats] = useState({
    projects: 0,
    clients: 0,
    experience: 0,
    githubStars: 0,
    technologies: 0,
    contributions: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch GitHub stats
        const githubResponse = await apiService.getGitHubStats();
        // Fetch projects
        const projectsResponse = await apiService.getProjects();
        
        setStats({
          projects: projectsResponse.data?.length || 15,
          clients: 10,
          experience: 3,
          githubStars: githubResponse.data?.stars || 0,
          technologies: 20,
          contributions: githubResponse.data?.contributions || 0
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Fallback stats
        setStats({
          projects: 15,
          clients: 10,
          experience: 3,
          githubStars: 0,
          technologies: 20,
          contributions: 0
        });
      }
    };
    fetchStats();
  }, []);

  const statItems = [
    { 
      icon: FaProjectDiagram, 
      label: 'Projects Built', 
      value: stats.projects,
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      icon: FaUsers, 
      label: 'Happy Clients', 
      value: stats.clients,
      color: 'from-green-500 to-emerald-500'
    },
    { 
      icon: FaCalendarAlt, 
      label: 'Years Experience', 
      value: stats.experience + '+',
      color: 'from-purple-500 to-pink-500'
    },
    { 
      icon: FaStar, 
      label: 'GitHub Stars', 
      value: stats.githubStars,
      color: 'from-yellow-500 to-orange-500'
    },
    { 
      icon: FaCode, 
      label: 'Technologies', 
      value: stats.technologies + '+',
      color: 'from-red-500 to-rose-500'
    },
    { 
      icon: FaGithub, 
      label: 'Contributions', 
      value: stats.contributions + '+',
      color: 'from-indigo-500 to-violet-500'
    },
  ];

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Achievements
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold">
            Impact in <span className="gradient-text">Numbers</span>
          </h2>
          <p className="text-gray-400 mt-2 max-w-2xl mx-auto">
            A snapshot of my journey and contributions to the tech ecosystem
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-all duration-300 group"
            >
              <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-r ${item.color} bg-opacity-20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}>
                <item.icon className="text-white text-xl" />
              </div>
              <div className="text-2xl font-bold text-white mb-1">
                {item.value}
              </div>
              <div className="text-xs text-gray-400">
                {item.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;